import { CSSProperties, forwardRef } from "react";
import dayjs from "dayjs";

interface ScrubPdfRow {
  claimUuid: string;
  claimId: string;
  patientName: string;
  passed: boolean;
  errorsFound: number;
  errors: { field?: string; message?: string }[];
}

interface ScrubPdfTemplateProps {
  totalCount: number;
  passedCount: number;
  failedCount: number;
  notFoundCount: number;
  totalIssues: number;
  rows: ScrubPdfRow[];
  width?: number;
}

const flexBetween: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
};

const badgeBase: CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "10px",
  fontSize: "10px",
  fontWeight: 600,
};

const PASSED_BADGE: CSSProperties = {
  ...badgeBase,
  background: "#E5F8E7",
  color: "#00831a",
};

const FAILED_BADGE: CSSProperties = {
  ...badgeBase,
  background: "#FFE5E5",
  color: "#c00",
};

const styles: Record<string, CSSProperties> = {
  page: {
    boxSizing: "border-box",
    padding: "28px 30px",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: "#1f1f1f",
    background: "#ffffff",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  headerWrap: {
    ...flexBetween,
    alignItems: "flex-end",
    borderBottom: "2px solid #1976d2",
    paddingBottom: "12px",
    marginBottom: "20px",
  },
  title: { fontSize: "22px", fontWeight: 700, margin: 0, color: "#1976d2" },
  subtitle: { fontSize: "11px", color: "#666", margin: "4px 0 0 0" },
  metaCol: { textAlign: "right", fontSize: "11px", color: "#444" },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "10px",
    marginBottom: "24px",
  },
  summaryCard: {
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "12px",
    textAlign: "center",
  },
  summaryValue: { fontSize: "22px", fontWeight: 700, margin: "0 0 4px 0" },
  summaryLabel: { fontSize: "11px", color: "#555", margin: 0 },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 700,
    margin: "0 0 10px 0",
    color: "#222",
    borderLeft: "3px solid #1976d2",
    paddingLeft: "8px",
  },
  table: { width: "100%", borderCollapse: "collapse", marginBottom: "24px" },
  th: {
    background: "#f4f6f8",
    border: "1px solid #d0d7de",
    padding: "8px 10px",
    fontSize: "11px",
    fontWeight: 700,
    textAlign: "left",
    color: "#222",
  },
  td: {
    border: "1px solid #e1e4e8",
    padding: "8px 10px",
    fontSize: "11px",
    verticalAlign: "top",
  },
  errorBlock: {
    border: "1px solid #e1e4e8",
    borderLeft: "3px solid #d32f2f",
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "12px",
    background: "#fff8f8",
    pageBreakInside: "avoid",
  },
  errorHeader: { ...flexBetween, alignItems: "center", marginBottom: "6px" },
  errorHeaderTitle: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#222",
    margin: 0,
  },
  errorMeta: { fontSize: "10px", color: "#666" },
  errorList: { margin: 0, paddingLeft: "18px" },
  errorItem: { fontSize: "11px", color: "#444", marginBottom: "2px" },
  errorField: { fontWeight: 700, color: "#d32f2f", marginRight: "4px" },
  footer: {
    borderTop: "1px solid #e1e4e8",
    paddingTop: "10px",
    marginTop: "8px",
    fontSize: "10px",
    color: "#777",
    textAlign: "center",
  },
};

const TABLE_COLS: { label: string; width: string }[] = [
  { label: "Claim ID", width: "14%" },
  { label: "Patient Name", width: "28%" },
  { label: "Status", width: "12%" },
  { label: "Errors Found", width: "12%" },
  { label: "Summary", width: "34%" },
];

const pad2 = (n: number) => String(n).padStart(2, "0");

const ScrubResultsPdfTemplate = forwardRef<
  HTMLDivElement,
  ScrubPdfTemplateProps
>(
  (
    {
      totalCount,
      passedCount,
      failedCount,
      notFoundCount,
      totalIssues,
      rows,
      width = 1040,
    },
    ref
  ) => {
    const generatedAt = dayjs().format("MMM DD, YYYY hh:mm A");
    const failedRows = rows.filter((r) => r.errors.length > 0);

    const summaryCards: {
      value: number;
      label: string;
      border: string;
      color: string;
    }[] = [
      {
        value: totalCount,
        label: "Claims Validated",
        border: "#90CAF9",
        color: "#1976d2",
      },
      {
        value: failedCount,
        label: "Claims with Errors",
        border: "#EF5350",
        color: "#d32f2f",
      },
      {
        value: passedCount,
        label: "Clean Claims",
        border: "#66BB6A",
        color: "#2e7d32",
      },
      {
        value: totalIssues,
        label: "Total Issues Found",
        border: "#FFA726",
        color: "#f57c00",
      },
    ];

    return (
      <div ref={ref} style={{ ...styles.page, width: `${width}px` }}>
        <div style={styles.headerWrap}>
          <div>
            <h1 style={styles.title}>Claims Scrub Summary</h1>
            <p style={styles.subtitle}>
              Bulk validation report for selected claims
            </p>
          </div>
          <div style={styles.metaCol}>
            <div>
              <strong>Generated:</strong> {generatedAt}
            </div>
            <div>
              <strong>Total Claims:</strong> {totalCount}
            </div>
          </div>
        </div>

        <div style={styles.summaryGrid}>
          {summaryCards.map((card) => (
            <div
              key={card.label}
              style={{ ...styles.summaryCard, borderColor: card.border }}
            >
              <p style={{ ...styles.summaryValue, color: card.color }}>
                {pad2(card.value)}
              </p>
              <p style={styles.summaryLabel}>{card.label}</p>
            </div>
          ))}
        </div>

        {notFoundCount > 0 && (
          <p style={{ fontSize: "11px", color: "#b00", marginTop: "-12px" }}>
            <strong>Note:</strong> {notFoundCount} claim
            {notFoundCount === 1 ? " was" : "s were"} not found.
          </p>
        )}

        <h2 style={styles.sectionTitle}>Detailed Results</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              {TABLE_COLS.map((c) => (
                <th key={c.label} style={{ ...styles.th, width: c.width }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={TABLE_COLS.length}
                  style={{ ...styles.td, textAlign: "center" }}
                >
                  No results.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.claimUuid}>
                <td style={styles.td}>{row.claimId || "-"}</td>
                <td style={styles.td}>{row.patientName || "-"}</td>
                <td style={styles.td}>
                  <span style={row.passed ? PASSED_BADGE : FAILED_BADGE}>
                    {row.passed ? "Passed" : "Failed"}
                  </span>
                </td>
                <td style={styles.td}>
                  {row.errorsFound > 0 ? pad2(row.errorsFound) : "-"}
                </td>
                <td style={styles.td}>
                  {row.passed
                    ? "No issues detected."
                    : `${row.errorsFound} issue${
                        row.errorsFound === 1 ? "" : "s"
                      } found — see details below.`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {failedRows.length > 0 && (
          <>
            <h2 style={styles.sectionTitle}>Error Details</h2>
            {failedRows.map((row) => (
              <div key={`err-${row.claimUuid}`} style={styles.errorBlock}>
                <div style={styles.errorHeader}>
                  <p style={styles.errorHeaderTitle}>
                    {row.patientName}{" "}
                    <span style={styles.errorMeta}>
                      (Claim ID: {row.claimId})
                    </span>
                  </p>
                  <span style={FAILED_BADGE}>
                    {pad2(row.errorsFound)} Error
                    {row.errorsFound === 1 ? "" : "s"}
                  </span>
                </div>
                <ul style={styles.errorList}>
                  {row.errors.map((err, idx) => (
                    <li key={idx} style={styles.errorItem}>
                      {err.field && (
                        <span style={styles.errorField}>{err.field}:</span>
                      )}
                      {err.message ?? ""}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}

        <div style={styles.footer}>
          Claims Scrub Summary • Generated {generatedAt}
        </div>
      </div>
    );
  }
);

ScrubResultsPdfTemplate.displayName = "ScrubResultsPdfTemplate";

export default ScrubResultsPdfTemplate;
